import React, { useEffect, useMemo, useRef } from "react";
import { Box } from "@mui/material";
import { gsap } from "gsap";

export default function LoadingScreen({ finish = false, onDone = () => {} }) {
  const rootRef = useRef(null);
  const qrRef = useRef(null);
  const pulseRef = useRef(null);

  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => (document.body.style.overflow = prev);
  }, []);

  const pixels = useMemo(() => {
    const size = 21;
    const arr = [];
    for (let y = 0; y < size; y++) {
      for (let x = 0; x < size; x++) {
        const filled =
          Math.random() > 0.55 ||
          (x < 7 && y < 7) ||
          (x > 13 && y < 7) ||
          (x < 7 && y > 13);
        arr.push({ x, y, filled });
      }
    }
    return arr;
  }, []);

  useEffect(() => {
    const pixelsEls = qrRef.current?.querySelectorAll(".qr-pixel");
    if (!pixelsEls) return;

    gsap.set(pixelsEls, { opacity: 0, scale: 0.2 });

    gsap.to(pixelsEls, {
      opacity: 1,
      scale: 1,
      duration: 0.35,
      stagger: {
        each: 0.006,
        from: "random",
      },
      ease: "power2.out",
    });

    // idle pulse
    pulseRef.current = gsap.to(qrRef.current, {
      scale: 1.01,
      duration: 1,
      repeat: -1,
      yoyo: true,
      ease: "power1.inOut",
      delay: 0.6,
    });

    return () => pulseRef.current?.kill();
  }, []);

  useEffect(() => {
    if (!finish) return;

    pulseRef.current?.kill();

    const tl = gsap.timeline({
      defaults: { ease: "power3.inOut" },
      onComplete: onDone,
    });

    tl.to(qrRef.current, {
      scale: 28,
      duration: 0.9,
      ease: "power3.in",
    });

    tl.to(rootRef.current, { opacity: 0, duration: 0.25 }, "-=0.15");

    return () => tl.kill();
  }, [finish, onDone]);

  return (
    <Box
      ref={rootRef}
      sx={{
        position: "fixed",
        inset: 0,
        zIndex: 20000,
        bgcolor: "var(--background)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        overflow: "hidden",
      }}
    >
      {/* QR CODE */}
      <Box
        ref={qrRef}
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(21, 1fr)",
          width: { xs: 95, md: 150 },
          aspectRatio: "1 / 1",
          gap: "2px",
          transformOrigin: "50% 50%",
        }}
      >
        {pixels.map((p, i) => (
          <Box
            key={i}
            className="qr-pixel"
            sx={{
              width: "100%",
              aspectRatio: "1 / 1",
              bgcolor: p.filled ? "var(--branco)" : "transparent",
              borderRadius: "2px",
            }}
          />
        ))}
      </Box>
    </Box>
  );
}
